/**
 * Horizontal date strip for the queue details page — mirrors the mobile
 * DateSelector: a calendar button (jump to any date) + scrollable day chips with
 * month separators. Colors/states match mobile:
 *   selected → primary (blue) · holiday → amber · closed weekday → greyed/disabled.
 */
import React, { useMemo, useRef, useEffect } from 'react';
import { Box, IconButton, Typography, Tooltip } from '@mui/material';
import { CalendarTodayRounded, CloseRounded } from '@mui/icons-material';
import { colors } from '../../theme/theme';

interface HolidayRange { start_date: string; end_date: string; }

interface Props {
  selectedDate: Date;
  onSelect: (d: Date) => void;
  /** Weekday indices (0=Sun … 6=Sat) the queue is closed on → disabled chips. */
  disabledWeekdays?: number[];
  holidays?: HolidayRange[];
  pastDays?: number;   // default 7
  futureDays?: number; // default 30
}

const DAY = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MON = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const startOfDay = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

const QueueDateStrip: React.FC<Props> = ({ selectedDate, onSelect, disabledWeekdays = [], holidays = [], pastDays = 7, futureDays = 30 }) => {
  const selStr = ymd(selectedDate);
  const inputRef = useRef<HTMLInputElement>(null);
  const selectedRef = useRef<HTMLDivElement>(null);

  const today = startOfDay(new Date());
  // Visible strip range. The calendar (below) has NO past min — you can jump to
  // any earlier date; when the picked date is outside this range we show it as a
  // full-date pill instead of a chip (mirrors mobile's out-of-range behaviour).
  const rangeStartStr = ymd((() => { const d = new Date(today); d.setDate(d.getDate() - pastDays); return d; })());
  const maxStr = ymd((() => { const d = new Date(today); d.setDate(d.getDate() + futureDays); return d; })());
  const isOutOfRange = selStr < rangeStartStr || selStr > maxStr;

  const isHoliday = (s: string) => holidays.some((h) => s >= h.start_date && s <= h.end_date);

  // Build items: day chips with a month label before each month change.
  const items = useMemo(() => {
    const start = new Date(today); start.setDate(today.getDate() - pastDays);
    const out: Array<{ type: 'month'; label: string } | { type: 'date'; date: Date; str: string; dow: number }> = [];
    let lastMonth = -1;
    for (let i = 0; i < pastDays + futureDays; i++) {
      const d = new Date(start); d.setDate(start.getDate() + i);
      if (d.getMonth() !== lastMonth) { out.push({ type: 'month', label: MON[d.getMonth()] }); lastMonth = d.getMonth(); }
      out.push({ type: 'date', date: d, str: ymd(d), dow: d.getDay() });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pastDays, futureDays]);

  // Center the selected chip whenever it changes (incl. a calendar pick that
  // lands inside the strip). rAF ensures the chip is laid out before scrolling.
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      selectedRef.current?.scrollIntoView({ inline: 'start', block: 'nearest', behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(raf);
  }, [selStr, isOutOfRange]);

  const openCalendar = () => {
    const el = inputRef.current;
    if (!el) return;
    // @ts-ignore — showPicker is supported in modern browsers
    if (typeof el.showPicker === 'function') el.showPicker(); else el.click();
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', minWidth: 0 }}>
      {/* Calendar jump button + hidden native date input */}
      <Tooltip title="Pick a date">
        <IconButton onClick={openCalendar} sx={{ width: 56, height: 56, flexShrink: 0, borderRadius: '12px', border: `1px solid ${colors.border}`, bgcolor: colors.gray50, color: colors.primary }}>
          <CalendarTodayRounded />
        </IconButton>
      </Tooltip>
      {/* No `min` → calendar can jump to ANY past date; future capped at maxStr. */}
      <Box component="input" ref={inputRef} type="date" value={selStr} max={maxStr}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => { if (e.target.value) onSelect(new Date(e.target.value + 'T00:00:00')); }}
        sx={{ position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }} />

      {isOutOfRange ? (
        /* Picked a date outside the visible strip → show it + a "back to today" button */
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 56, px: 2, borderRadius: '12px', bgcolor: colors.primary, color: '#fff' }}>
          <Typography noWrap sx={{ fontWeight: 700 }}>
            {selectedDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </Typography>
          <Tooltip title="Back to today">
            <IconButton size="small" onClick={() => onSelect(today)} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,.2)', '&:hover': { bgcolor: 'rgba(255,255,255,.3)' } }}>
              <CloseRounded fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
      /* Scrollable strip — flex:1 + minWidth:0 so it scrolls INSIDE this box (not the page) */
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.5, overflowX: 'auto', py: 0.5, scrollbarWidth: 'none', msOverflowStyle: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
        {items.map((it, idx) => {
          if (it.type === 'month') {
            return (
              <Box key={`m-${idx}`} sx={{ width: 48, height: 56, flexShrink: 0, borderRadius: '10px', border: `1px solid ${colors.border}`, bgcolor: colors.gray50, display: 'grid', placeItems: 'center' }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: colors.textSecondary, transform: 'rotate(-90deg)' }}>{it.label}</Typography>
              </Box>
            );
          }
          const selected = it.str === selStr;
          const holiday = !selected && isHoliday(it.str);
          const disabled = disabledWeekdays.includes(it.dow);
          const bg = selected ? colors.primary : holiday ? '#FFFBEB' : colors.backgroundPaper;
          const border = selected ? colors.primary : holiday ? '#FCD34D' : colors.border;
          const dayCol = selected ? 'rgba(255,255,255,.85)' : holiday ? '#B45309' : colors.textSecondary;
          const numCol = selected ? '#fff' : holiday ? '#B45309' : colors.textPrimary;
          return (
            <Box
              key={it.str}
              ref={selected ? selectedRef : undefined}
              onClick={() => !disabled && onSelect(it.date)}
              sx={{
                width: 48, height: 56, flexShrink: 0, borderRadius: '10px', border: `1px solid ${border}`, bgcolor: bg,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px',
                cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.45 : 1,
                transition: 'background .12s, border-color .12s',
                '&:hover': disabled || selected ? undefined : { borderColor: colors.primary },
              }}
            >
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: disabled ? colors.gray400 : dayCol }}>{DAY[it.dow]}</Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: disabled ? colors.gray400 : numCol }}>{it.date.getDate()}</Typography>
            </Box>
          );
        })}
      </Box>
      )}
    </Box>
  );
};

export default QueueDateStrip;
